import { useEffect, useState } from 'react';
import { Shield } from 'lucide-react';
import { get } from '../../../../api/client';

// 条款配置（与后端 system-config terms namespace 对齐）
interface TermsConfig {
  title: string;
  content: string;
  countdownSeconds: number;
  version: string;
}

// /public/config 响应结构（具名 interface 避免泛型内联类型触发 hooks 依赖误报）
interface PublicConfigResponse {
  terms?: Partial<TermsConfig>;
}

// 硬编码兜底：后端未配置 terms 时使用（与原 Step1 内容一致）
const FALLBACK_TERMS: TermsConfig = {
  title: '平台使用规则',
  countdownSeconds: 60,
  version: '1.0',
  content: `系统干什么
1、本系统是订单预约与对账核算的在线管理工具，为您提供订单添加、查询、审核确认、对账抵消、客户管理与统计等服务。
2、本系统不撮合交易、不参与订单定价、不代收代付、不提供担保、不介入纠纷调解；订单内容由您与您的客户自行填写、自行确认，系统只据实记录与核算。
3、因订单填写错误、取消、改期或双方履约问题造成的损失，本系统不承担责任；重要数据请自行以多种方式备份（导出、线下另行留档）。
〔身份与实名核验〕
4、注册必须使用您本人的真实身份信息：姓名、身份证号、手机号、邮箱。这是您在系统内的唯一身份凭证，也是订单能正确归属、账目能算对的前提。
5、注册时您的姓名、身份证号、手机号会提交给第三方核验通道做一致性核对；核验可能不通过，未通过不代表您的身份有问题，可提交人工审核（上传身份证照片）由客服核实。
6、身份证号仅用于系统内的身份核验与比对，不用于展示，也不会被其他用户调用；系统不存储您的身份证号明文，只保存掩码、密文与用于比对的哈希值。
7、为控制成本与防止滥用，同一身份证号在短时间内多次核验未通过后，系统会暂停继续提交核验，并引导您提交人工审核。
8、经查实以虚假信息注册的，平台可停用或禁用该账号（禁用会记录原因），并按规则处理该账号产生的数据；由此给他人造成的损失由行为人承担。
〔客户档案与"首填机制"〕
9、系统按"首填机制"建立客户档案：不同的人添加同一个人时，会自动关联到同一份档案而不重复建档，以保证系统内身份唯一。
10、客户一经添加即为系统内的公共档案，不归添加者独占，其他用户也可将其绑定为自己的客户；请勿在客户档案中填写非必要的个人信息。
11、若您注册时系统提示您的信息已存在，通常是因为已有用户把您登记为客户，这不影响您注册；您可提交真实信息，由客服核实档案归属后认领该档案。
〔您的订单会被谁看到·订单能改什么〕
12、本系统用"订单"连接交易双方：您创建的订单，对方（客户或对端用户）在与该订单相关的范围内可见订单信息、金额、状态与审核记录；涉及人员调用（支出订单）时，被调用人员在必要范围内同样可见。这是双方对账所必需，不构成越权使用。
13、订单提交后，订单分类与预约日期不可更改；收入方向的订单不可改派客户。需要改期或更换交易对象，只能取消原订单后重新提交。系统内所有提交的订单不可删除，将保留留痕。
14、系统会对疑似重复的订单给出提示，但是否重复由您判断：可确认继续提交，也可撤回。提示本身不是判定。
15、付款状态由已记录的收款金额与订单金额比对得出，分为未付款、部分付款、已结清三档；对账抵消属尊享会员功能，抵消会更新相关订单的已付金额，提交前请核对无误。
〔账号安全·消息通知·费用与会员〕
16、密码由您自行设置并妥善保管，因密码泄露产生的损失由您承担；系统会记录登录设备信息，便于您核查并处置异常登录。经人工审核开通的账号，初始密码通过邮件下发，请在首次登录后立即修改。
17、为提供服务所必需，系统会通过站内通知、短信、邮件以及移动设备推送渠道，向您发送审核提醒、档期催办、结算与系统消息；在安卓端接入了华为、荣耀、OPPO、vivo、小米的手机厂商推送通道并内嵌其推送 SDK，鸿蒙端使用系统内置的华为推送服务；推送 SDK 会读取设备信息生成设备推送令牌，我们只把该令牌与通知的标题、正文交给对应厂商通道下发，通知正文只含订单编号、金额、档期日期与对方称呼等业务信息，不含身份证号；您可在手机系统设置中关闭本应用的通知权限。
18、免费注册即可使用订单、客户管理与基础统计；对账抵消、导出电子表格、打印与导出 PDF/PNG、分享订单摘要、高级数据分析、数据备份与迁移为尊享会员功能。会员费通过第三方持牌支付机构收取，本系统不自行归集或保管您的资金；除服务存在严重问题或法律要求外不支持退款，会员到期未续费仅停止付费功能，您的数据仍可在免费范围内查看。
19、您可随时申请注销，注销不可撤销，请先自行导出数据备份。注销时：与注册用户存在交互的订单不会随注销消失（那是双方的对账凭据）；本人孤立订单（仅本人提交、且对方不是注册用户的订单）随注销同步删除；注销时若本人添加的客户在系统内已无其他用户绑定，该孤立客户一并删除。
20、注销会清除您的登录凭据与用户名。若您已被其他用户绑定为客户，为保留他人的对账凭据，您的手机号、邮箱及身份核验用的比对值可能继续保留在客户档案中；平台不会因此恢复您的登录能力，也不会据此向您推送营销信息。
21、本页面需您滚动阅读至最后一行，并在阅读倒计时结束后才能勾选同意。勾选即表示您已阅读并同意本条款，以及与本条款一并适用的《服务条款》和《隐私政策》；条款内容以本页当前展示的版本为准。
注意：系统只是给您提供了一个方便的、能实时管理订单的网络信息平台，希望您在使用便捷的同时，也不要完全依赖本系统。我们无法保证订单的保存绝对安全，请谨慎使用做好备份！感谢您的到来和使用!`,
};

// Step 1: 注册必读（条款由后台配置下发，未配置时使用内置兜底）
export function Step1RegisterGuide({
  agreed,
  setAgreed,
  hasReadToBottom,
  setHasReadToBottom,
  countdown,
}: {
  agreed: boolean;
  setAgreed: (value: boolean) => void;
  hasReadToBottom: boolean;
  setHasReadToBottom: (value: boolean) => void;
  countdown: number;
}) {
  const [terms, setTerms] = useState<TermsConfig>(FALLBACK_TERMS);

  useEffect(() => {
    let cancelled = false;
    get<PublicConfigResponse>('/public/config')
      .then((config) => {
        if (cancelled || !config?.terms?.content) return;
        setTerms({
          title: config.terms.title || FALLBACK_TERMS.title,
          content: config.terms.content,
          countdownSeconds: config.terms.countdownSeconds ?? FALLBACK_TERMS.countdownSeconds,
          version: config.terms.version || FALLBACK_TERMS.version,
        });
      })
      .catch(() => {
        // 拉取失败静默使用兜底条款
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-4">
      {countdown > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
          <Shield className="w-5 h-5 text-amber-500" />
          <p className="text-sm text-amber-700">
            请仔细阅读，关系到您是否适合使用本平台
          </p>
        </div>
      )}

      <div
        onScroll={(e) => {
          const target = e.target as HTMLElement;
          if (target.scrollHeight - target.scrollTop - target.clientHeight < 50) {
            setHasReadToBottom(true);
          }
        }}
        className="border border-slate-200 rounded-xl p-4 max-h-64 overflow-y-auto text-sm text-slate-600 bg-slate-50 leading-relaxed whitespace-pre-wrap"
      >
        <h5 className="font-medium text-slate-800 mb-3">
          {terms.title}
          {terms.version && terms.version !== '1.0' && (
            <span className="ml-2 text-xs text-slate-400">v{terms.version}</span>
          )}
        </h5>
        {terms.content}

        <div className="mt-6 pt-4 border-t border-slate-200">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={hasReadToBottom}
              disabled
              className="w-4 h-4 text-teal-500 rounded border-slate-300"
            />
            <span className={`text-sm ${hasReadToBottom ? 'text-green-600' : 'text-slate-400'}`}>
              {hasReadToBottom ? '✓ 已阅读到内容末尾' : '请继续阅读...'}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          id="agree-terms"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-teal-500 rounded border-slate-300"
        />
        <label htmlFor="agree-terms" className="text-sm text-slate-600 cursor-pointer hover:text-slate-800 transition-colors">
          我已阅读并同意 <span className="text-teal-600 font-medium">《服务条款》</span> 和 <span className="text-teal-600 font-medium">《隐私政策》</span>，了解平台使用规则
        </label>
      </div>
    </div>
  );
}